import { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configuration du worker PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface PDFViewerProps {
  pdfUrl: string;
  onClose: () => void;
}

const LoaderSpinner = () => (
  <div className="flex justify-center items-center p-8">
    <div className="relative">
      <div className="w-12 h-12 rounded-full absolute border-4 border-solid border-gray-200"></div>
      <div className="w-12 h-12 rounded-full animate-spin absolute border-4 border-solid border-[#45B8A9] border-t-transparent"></div>
    </div>
  </div>
);

const PDFViewer: React.FC<PDFViewerProps> = ({ pdfUrl, onClose }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState(1.2);
  const [error, setError] = useState<string | null>(null);

  // Gestion intelligente de l'URL du PDF selon l'environnement
  const fullPdfUrl = pdfUrl.startsWith('http') 
    ? pdfUrl 
    : `${window.location.origin}${pdfUrl}`;

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
    setError(null);
  }

  function onDocumentLoadError(error: Error): void {
    console.error('Erreur de chargement du PDF:', error);
    setError(`Impossible de charger le PDF. Erreur: ${error.message}`);
  }

  // Ajuster l'échelle en fonction de la largeur de l'écran
  const adjustScale = () => {
    if (window.innerWidth < 640) { // sm
      setScale(0.6);
    } else if (window.innerWidth < 768) { // md
      setScale(0.8);
    } else {
      setScale(1.2);
    }
  };

  // Écouter les changements de taille d'écran
  useEffect(() => {
    adjustScale();
    window.addEventListener('resize', adjustScale);
    return () => window.removeEventListener('resize', adjustScale);
  }, []);

  return (
    <div className="fixed inset-0 z-[1001] bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-xl p-3 sm:p-6 w-full max-w-4xl max-h-[90vh] overflow-auto relative">
        {/* Bouton fermer en position absolue avec plus d'espace */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 p-2 rounded-full hover:bg-gray-100 transition-colors bg-white shadow-md"
          aria-label="Fermer"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Navigation avec plus d'espace en haut */}
        <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-2 mb-4 mt-12 sm:mt-14">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}
              disabled={pageNumber <= 1}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#45B8A9] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Précédent
            </button>
            <button
              onClick={() => setPageNumber(prev => Math.min(numPages, prev + 1))}
              disabled={pageNumber >= numPages}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#45B8A9] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant
              <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <span className="text-sm text-gray-600 font-medium sm:mr-12">
            Page {pageNumber} sur {numPages}
          </span>
        </div>

        {/* PDF Viewer */}
        <div className="relative flex justify-center mt-4">
          <Document
            file={fullPdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            className="flex justify-center"
            loading={<LoaderSpinner />}
            error={
              <div className="text-center text-red-500 p-4 flex items-center justify-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error || 'Une erreur est survenue lors du chargement du PDF'}
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="border shadow-lg rounded-lg"
              scale={scale}
              loading={<LoaderSpinner />}
            />
          </Document>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer; 